import { X } from "lucide-react";
import BackNavigator from "./components/back-navigation";
import { InputWithLabel } from "./components/input-label";
import { useForm } from "@tanstack/react-form";
import { Button } from "@/components/ui/button";
import { messageSupportSchema } from "@/lib/form-schema";
import { cn, describeMissingFields } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import TextAreaInput from "./components/input-textarea";
import { useMFeedback } from "@/hooks/use-feedback";
import { useGroupProvider } from "@/context/group-provider";
import { AlignError } from "@/constants";
import IDENTIFIERS from "@/constants/test_id";
import { useEffect } from "react";
import {
  loggerContextManager,
  type PanelContext,
} from "@/services/logger/smart-context-provider";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";

const MessageSupport = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
}) => {
  const { groupData } = useGroupProvider();
  const feedbackQuery = useMFeedback();

  // Panel context for Message Support bottom sheet (support flow)
  useEffect(() => {
    const panelContext: PanelContext = {
      panelName: "Message Support",
      tag: LogTags.SUPPORT,
      category: LogCategories.MESSAGE_SUPPORT,
      subCategory: LogSubCategories.GROUP_INFO_MODAL,
    };
    loggerContextManager.setPanelContext(panelContext);
    return () => {
      loggerContextManager.clearPanelContext();
    };
  }, []);

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
    onSubmit: async () => {
      if (!groupData) return;
      feedbackQuery.mutate({
        groupId: groupData.group_id,
        loginGroupId: groupData.entered_id,
        name: form.state.values.name,
        email: form.state.values.email,
        message: form.state.values.message,
      });
      form.reset();
    },
  });

  return (
    // 1.3.1 Info and Relationships — A: this is only ever rendered inside GroupInfo,
    // which is only ever rendered inside login.tsx's own <main> — a nested <main>
    // would be an invalid, duplicate landmark.
    <div className="">
      <BackNavigator
        title="Send Message to Customer Support"
        right={
          <Button
            variant="ghost"
            aria-label="Close"
            className="h-auto w-fit p-0 hover:bg-transparent"
            onClick={() => {
              setIsOpen(!isOpen);
              form.reset();
            }}
            data-testid={IDENTIFIERS.MESSAGE_SUPPORT.CLOSE_BUTTON}
          >
            <X className="cursor-pointer" size={20} />
          </Button>
        }
      />
      <form
        className="mt-20 flex min-w-[80%] flex-col space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.Field
          name="name"
          validators={{
            // 3.3.1 Error Identification — A: validating onChange committed the error
            // asynchronously relative to leaving the field — mid-keystroke rather than
            // at blur — so its DOM update wasn't synchronized with the Tab-triggered
            // focus move to the next field, letting a screen reader announce the next
            // field before (or interleaved with) this one's error. onBlur runs as part
            // of the same synchronous handler that fires right before focus moves on,
            // matching every other form in this app (login, group, locate, etc).
            onBlur: ({ value }) => {
              const result = messageSupportSchema.shape.name.safeParse(value);
              return result.success
                ? undefined
                : result.error.errors[0].message;
            },
          }}
        >
          {(field) => (
            <div>
              <InputWithLabel
                id="name"
                label="Name"
                placeholder="Name"
                value={field.state.value}
                onChange={(newValue) => field.handleChange(newValue)}
                onBlur={() => field.handleBlur()}
                error={field.state.meta.errors[0]}
                alignError={AlignError.END}
                data-testid={IDENTIFIERS.MESSAGE_SUPPORT.NAME_INPUT}
              />
            </div>
          )}
        </form.Field>

        <div className="mb-4 flex flex-col gap-2">
          <form.Field
            name="email"
            validators={{
              // 3.3.1 Error Identification — A: see the "name" field's comment above.
              onBlur: ({ value }) => {
                const result =
                  messageSupportSchema.shape.email.safeParse(value);
                return result.success
                  ? undefined
                  : result.error.errors[0].message;
              },
            }}
          >
            {(field) => (
              <div className="">
                <InputWithLabel
                  id="email"
                  label="Email"
                  placeholder="Email"
                  value={field.state.value}
                  onChange={(newValue) => field.handleChange(newValue)}
                  onBlur={() => field.handleBlur()}
                  error={field.state.meta.errors[0]}
                  alignError={AlignError.END}
                  data-testid={IDENTIFIERS.MESSAGE_SUPPORT.EMAIL_INPUT}
                />
              </div>
            )}
          </form.Field>
        </div>

        <div className="mb-4 flex flex-col gap-2">
          <form.Field
            name="message"
            validators={{
              // 3.3.1 Error Identification — A: see the "name" field's comment above.
              onBlur: ({ value }) => {
                const result =
                  messageSupportSchema.shape.message.safeParse(value);
                return result.success
                  ? undefined
                  : result.error.errors[0].message;
              },
            }}
          >
            {(field) => (
              <div className="flex w-full flex-col gap-4">
                {/* 3.3.2 Labels or Instructions — A / 4.1.2 Name, Role, Value — A: had
                    no htmlFor, so the field had no accessible name at all. */}
                <Label htmlFor="message">Message</Label>
                <TextAreaInput
                  id="message"
                  value={field.state.value}
                  placeholder="Message"
                  className="min-h-48 resize-none border-0 bg-gray-100"
                  onChange={(newValue) => field.handleChange(newValue)}
                  onBlur={() => field.handleBlur()}
                  error={field.state.meta.errors[0]}
                  alignError={AlignError.END}
                  data-testid={IDENTIFIERS.MESSAGE_SUPPORT.MESSAGE_INPUT}
                />
              </div>
            )}
          </form.Field>
        </div>

        <div className="flex w-full justify-center gap-6 pt-4">
          <div className="">
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.values.name,
                state.values.email,
                state.values.message,
              ]}
            >
              {([canSubmit, name, email, message]) => {
                // 3.3.1 Error Identification — A. Native disabled on !canSubmit
                // removed this from the tab order — aria-disabled keeps it
                // reachable, the description leads with "Disabled" since
                // aria-disabled's own announcement isn't consistently supported.
                // Native disabled stays for the one case where fully blocking is
                // correct: a request already in flight.
                const requirement =
                  describeMissingFields([
                    { label: "name", value: name },
                    { label: "email", value: email },
                    { label: "message", value: message },
                  ]) || "Disabled. Fix the errors above to continue.";
                const isDisabled = !canSubmit || !name || !email || !message;
                return (
                  <>
                    <Button
                      size="lg"
                      variant="default"
                      className={cn(
                        "bg-primary hover:bg-primary-selected",
                        isDisabled && "opacity-50",
                      )}
                      type="submit"
                      disabled={feedbackQuery.isPending}
                      aria-disabled={isDisabled}
                      aria-describedby={
                        isDisabled ? "send-message-requirement" : undefined
                      }
                      data-testid={
                        IDENTIFIERS.MESSAGE_SUPPORT.SEND_MESSAGE_BUTTON
                      }
                    >
                      {feedbackQuery.isPending
                        ? "Sending ..."
                        : " Send Message"}
                    </Button>
                    {isDisabled && (
                      <p id="send-message-requirement" className="sr-only">
                        {requirement}
                      </p>
                    )}
                  </>
                );
              }}
            </form.Subscribe>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MessageSupport;
